import type {
  IProdoqCompanyResponse,
  IProdoqCompanyUpdatePayload,
  IProdoqSupportedLanguage,
} from '../../../../../shared/types/prodoq';

export const PRODOQ_SUPPORTED_LANGUAGE_OPTIONS: IProdoqSupportedLanguage[] = [
  { label: '한국어', value: 'ko-KR' },
  { label: 'English', value: 'en-US' },
  { label: 'Español', value: 'es-ES' },
];

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const getSupportedLanguageLabel = (value: string, label?: unknown): string => {
  if (typeof label === 'string' && label.trim()) {
    return label;
  }

  return PRODOQ_SUPPORTED_LANGUAGE_OPTIONS.find((option) => option.value === value)?.label ?? value;
};

const parseSupportedLanguageString = (supportedLanguages: string): unknown => {
  const trimmedSupportedLanguages = supportedLanguages.trim();

  if (!trimmedSupportedLanguages) {
    return [];
  }

  try {
    return JSON.parse(trimmedSupportedLanguages);
  } catch {
    return trimmedSupportedLanguages.split(',').map((value) => value.trim());
  }
};

const normalizeSupportedLanguageItem = (language: unknown): IProdoqSupportedLanguage[] => {
  if (typeof language === 'string') {
    const value = language.trim();

    if (!value) {
      return [];
    }

    return [{ label: getSupportedLanguageLabel(value), value }];
  }

  if (!isObject(language) || typeof language.value !== 'string') {
    return [];
  }

  return [
    {
      label: getSupportedLanguageLabel(language.value, language.label),
      value: language.value,
    },
  ];
};

export const normalizeProdoqSupportedLanguages = (supportedLanguages: unknown): IProdoqSupportedLanguage[] => {
  if (typeof supportedLanguages === 'string') {
    return normalizeProdoqSupportedLanguages(parseSupportedLanguageString(supportedLanguages));
  }

  if (Array.isArray(supportedLanguages)) {
    return supportedLanguages
      .flatMap((language) => normalizeSupportedLanguageItem(language))
      .filter((language, index, languages) => languages.findIndex((item) => item.value === language.value) === index);
  }

  return normalizeSupportedLanguageItem(supportedLanguages);
};

export const getProdoqSupportedLanguageOptions = (supportedLanguages: unknown): IProdoqSupportedLanguage[] => {
  return normalizeProdoqSupportedLanguages(supportedLanguages).reduce<IProdoqSupportedLanguage[]>((options, language) => {
    const hasLanguage = options.some((option) => option.value === language.value);

    if (hasLanguage) {
      return options;
    }

    return [...options, language];
  }, PRODOQ_SUPPORTED_LANGUAGE_OPTIONS);
};

export const createProdoqCompanyUpdatePayload = (
  company: IProdoqCompanyResponse,
  overrides: Partial<IProdoqCompanyUpdatePayload> = {},
): IProdoqCompanyUpdatePayload => ({
  pageLimit: company.pageLimit ?? 0,
  accountLimit: company.accountLimit ?? 0,
  dictionaryLimit: company.dictionaryLimit ?? 0,
  dailyChatTurnLimit: company.dailyChatTurnLimit ?? 0,
  feedbackManageYn: company.feedbackManageYn,
  supportedLanguages: normalizeProdoqSupportedLanguages(company.supportedLanguages),
  ...overrides,
});
